import clsx from "clsx";
import graphStyles from "../graphStyles.module.css";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Prefixes } from "../../../../Tools/ProjectX";
import smetaAxios from "../../../../../SmetaAxios";
import { SectionGraphContext } from "../SectionGraph";
import CheckBoxMultiCheck from "../../CheckBoxMultiCheck";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

interface FieldsData {
  stash: string[];
  coll: string[];
  row: string[];
}

const buildPivotTable = (data: {
  viewIdent?: string;
  cols?: string;
  rows?: string;
}): Promise<{ Chart: any; Table: { RCDATA: string } }> | undefined => {
  if (!data.viewIdent) return;
  const params = {
    Cols: data.cols,
    Rows: data.rows,
    ViewIdent: data.viewIdent,
    WSM: "1",
  };
  const config = {
    prefix: Prefixes.cubes,
    method: "GET",
    url: "BuildPivotTable",
    params,
  };
  return smetaAxios(config);
};

const GraphTable = () => {
  const context = useContext(SectionGraphContext) || {};
  const { cols, fields, rows, table, viewIdent, setCharts } = context;

  const fieldsDataRef = useRef<{ [key: string]: string[] } & FieldsData>({
    stash: Object.keys(fields || []),
    coll: Object.keys(cols || []),
    row: Object.keys(rows || []),
  });

  const [fieldsState, setFieldsState] = useState(fieldsDataRef.current);
  const [tableData, setTableData] = useState<{ RCDATA?: string }>(table || {});
  const [autoUpdate, setAutoUpdate] = useState<string>("0");

  const dragLabelRef = useRef<string | null>(null);
  const dragStartField = useRef<string | null>(null);
  const mainContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onDragEnd();
  }, [autoUpdate]);

  const updateFields = () => {
    setFieldsState({ ...fieldsDataRef.current });
  };

  const onCheck = (data: { CheckState: string }) => {
    setAutoUpdate(data.CheckState);
  };

  const onDragStart = (ev: any) => {
    dragLabelRef.current = ev.target.id;
    dragStartField.current = ev.target.dataset.field;
  };

  const onDragEnd = async (force?: boolean) => {
    updateFields();
    if (!force && autoUpdate === "0") return;

    dragLabelRef.current = null;
    dragStartField.current = null;
    const reqData = {
      cols: fieldsDataRef.current.coll.join(","),
      rows: fieldsDataRef.current.row.join(","),
      viewIdent: viewIdent,
    };
    const data = await buildPivotTable(reqData);
    if (data) {
      setCharts?.(data.Chart);
      setTableData({ ...data.Table });
    }
  };

  const onDragEnter = (ev: any, fieldKey: string) => {
    if (!dragLabelRef.current) return;

    const target = ev.target;

    let startField = dragStartField.current;
    const dragLabel = dragLabelRef.current;

    if (startField && startField !== fieldKey && dragLabel) {
      const newArr = deleteField(dragLabel, startField);
      fieldsDataRef.current[startField] = newArr;
    }

    if (!fieldsDataRef.current[fieldKey].includes(dragLabelRef.current)) {
      const array = fieldsDataRef.current[fieldKey];
      dragStartField.current = fieldKey;

      fieldsDataRef.current[fieldKey] = [...array, dragLabelRef.current];
    }

    const sortArr = arraySort(fieldsDataRef.current[fieldKey], target);
    fieldsDataRef.current[fieldKey] = sortArr;
  };

  const onDragOver = (ev: any) => {
    ev.preventDefault();
  };

  const arraySort = (arr: string[], target: any) => {
    const result = [...arr];
    if (!dragLabelRef.current || dragLabelRef.current === target.id) return arr;

    const indexDragEl = result.indexOf(dragLabelRef.current);
    const indexTargetEl = result.indexOf(target.id);
    if (target.dataset.last) {
      result.splice(indexDragEl, 1);
      result.push(dragLabelRef.current);
      return result;
    }
    result.splice(indexDragEl, 1, target.id);
    result.splice(indexTargetEl, 1, dragLabelRef.current);
    return result;
  };

  const deleteField = (label: string, fieldKey: string) => {
    const array = [...fieldsDataRef.current[fieldKey]];
    const index = array.indexOf(label);
    if (index < 0) return fieldsDataRef.current[fieldKey];
    array.splice(index, 1);
    return array;
  };

  const getField = (label: string, key: string, fieldKey: string) => {
    return (
      <div
        key={key}
        id={label}
        data-field={fieldKey}
        className={clsx(graphStyles.field)}
        draggable
        onDragEnter={(ev) => {
          onDragEnter(ev, fieldKey);
        }}
        onDragOver={onDragOver}
      >
        {label}
      </div>
    );
  };

  const getFieldsJSX = (key: string) => {
    let lastLabel;
    const result = fieldsState[key].map((item, index) => {
      if (index === fieldsState[key].length - 1) {
        lastLabel = item;
      }
      return getField(item, `${key}-field_${index}`, key);
    });

    result.push(
      <div
        id={lastLabel}
        data-field={key}
        data-last={"true"}
        key={`${key}-epmtydragzone`}
        onDragOver={onDragOver}
        onDragEnter={(ev) => {
          onDragEnter(ev, key);
        }}
        className={clsx(graphStyles.dragZone)}
      ></div>,
    );
    return result;
  };

  const stashFields = useMemo(() => {
    return getFieldsJSX("stash");
  }, [fieldsState]);

  const collFields = useMemo(() => {
    return getFieldsJSX("coll");
  }, [fieldsState]);

  const rowFields = useMemo(() => {
    return getFieldsJSX("row");
  }, [fieldsState]);

  const html = useMemo(() => {
    const testHtml = (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div>
          <iframe
            src={"data:application/pdf;base64," + tableData?.RCDATA}
            height="100%"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
        <div>
          <iframe src={atob(tableData?.RCDATA || "")} height="100%" style={{ width: "100%", height: "100%" }} />
        </div>
      </div>
    );
    return testHtml;
  }, [tableData]);

  return (
    <div
      ref={mainContainerRef}
      onDragStart={onDragStart}
      onDragEnd={() => onDragEnd()}
      className={clsx(graphStyles.mainGraphTableBlock)}
    >
      <div className={clsx(graphStyles.stashFieldsBlock)}>{stashFields}</div>
      <div className={clsx(graphStyles.graphTableContainer)}>
        <div className={clsx(graphStyles.graphToolsContainer)}>
          <div className={clsx(graphStyles.graphToolsContainer)}>
            <div className={clsx(graphStyles.graphToolLabel)}>Автообновление</div>
            <CheckBoxMultiCheck CheckState={autoUpdate} onEdit={onCheck} />
          </div>
          <IconButton onClick={() => onDragEnd(true)} className={clsx(graphStyles.graphToolButton)}>
            <RefreshIcon />
          </IconButton>
        </div>
        <div className={clsx(graphStyles.fieldsContainer)}>
          <div className={clsx(graphStyles.dragZoneLabel)}>Столбцы</div>
          <div className={clsx(graphStyles.dragZoneContainer)}>{collFields}</div>
        </div>
        <div className={clsx(graphStyles.fieldsContainer)}>
          <div className={clsx(graphStyles.dragZoneLabel)}>Строки</div>
          <div className={clsx(graphStyles.dragZoneContainer)}>{rowFields}</div>
        </div>
        <div className={clsx(graphStyles.tableContainer)}>{html}</div>
      </div>
    </div>
  );
};
export default GraphTable;
